import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {
  Button,
  ConfirmationModal,
  Header,
  Pagination,
  PermissionsProvider,
  SearchBar,
  TableSkeleton,
} from '@/components';
import type {PaginationProps} from '@/components/common/Pagination';
import {BusinessOnboardingStatus} from '@/types';

import CreateInviteModal from './CreateInviteModal';
import EditInviteModal from './EditInviteModal';
import InvitesTable from './InvitesTable';
import useBusinessInvites from './useBusinessInvites';

const CONFIRM_COPY = {
  approve: {
    title: 'Approve Invite',
    label:
      'The business will be emailed a link to set a password and set up their account.',
    actionLabel: 'Approve',
    loadingLabel: 'Approving...',
  },
  resend: {
    title: 'Resend Invite',
    label: 'A fresh invite link will be emailed to the business.',
    actionLabel: 'Resend',
    loadingLabel: 'Resending...',
  },
  deny: {
    title: 'Deny Invite',
    label:
      'The business will be told it cannot proceed, and the invite leaves this list.',
    actionLabel: 'Deny',
    loadingLabel: 'Denying...',
  },
} as const;

// An approved invite was told yes, so denying it sends nothing: it is for one
// found to duplicate a business already on Afriex.
const DENY_APPROVED_COPY = {
  ...CONFIRM_COPY.deny,
  label:
    'The invite leaves this list and its link stops working. The business is not emailed.',
};

const BusinessInvitesScreen = () => {
  const hook = useBusinessInvites();
  // Null while the modal is closed; any entry works as the default.
  const confirmCopy =
    hook.pendingAction === 'deny' &&
    hook.selectedInvite?.meta?.status === BusinessOnboardingStatus.APPROVED
      ? DENY_APPROVED_COPY
      : CONFIRM_COPY[hook.pendingAction ?? 'resend'];

  return (
    <main className="my-5 mx-4 md:mx-10">
      <div className="flex items-center justify-between">
        <Header title="Business Invites" />
        <PermissionsProvider
          permission={AfriexAdminPermission.BusinessOnboardingManage}
        >
          <Button colorScheme="cyan" onClick={hook.openCreateModal}>
            Create Invite
          </Button>
        </PermissionsProvider>
      </div>

      <div className="my-4 max-w-md">
        <SearchBar
          placeholder="Search by Business Name, Full Name, or Email"
          handleSearch={hook.handleSearch}
          handleAutoComplete={hook.handleSearch}
          clearFilters={hook.clearFilters}
        />
      </div>

      {hook.isLoading ? (
        <TableSkeleton />
      ) : (
        <InvitesTable
          list={hook.invites}
          onAction={hook.openConfirm}
          onEdit={hook.openEditModal}
        />
      )}
      {!hook.isSearchActive && hook.invites.length > 0 && (
        <Pagination
          onPageChange={hook.fetchInvites as PaginationProps['onPageChange']}
          total={hook.total}
          isPageReset={hook.isPageReset}
          currentPageOverride={hook.displayPage}
        />
      )}

      <ConfirmationModal
        isOpen={hook.isConfirmOpen}
        title={confirmCopy.title}
        label={confirmCopy.label}
        confirmAction={hook.handleConfirmAction}
        confirmActionLabel={confirmCopy.actionLabel}
        cancelAction={hook.closeConfirm}
        cancelActionLabel="Cancel"
        loading={hook.isActionLoading}
        loadingLabel={confirmCopy.loadingLabel}
      />

      <EditInviteModal
        isOpen={hook.isEditOpen}
        onClose={hook.closeEditModal}
        invite={hook.selectedInvite}
        onSubmit={hook.handleEditInvite}
        isLoading={hook.isActionLoading}
      />

      <CreateInviteModal
        isOpen={hook.isCreateOpen}
        onClose={hook.closeCreateModal}
        onSubmit={hook.handleCreateInvite}
        isLoading={hook.isActionLoading}
      />
    </main>
  );
};

export default BusinessInvitesScreen;
